/**
 * Feature Flag Registry
 *
 * Single source of truth for ALL feature flags consumed by SMP Platform.
 * To add a new flag:
 *   1. Add an entry here with description, defaultEnabled, isStoreScoped, and metadata.cti
 *   2. Create the flag in AppConfig (backend)
 *   3. Use `useFeatureFlag('your_flag_name')` in your component
 *
 * Registration and value resolution fail DIFFERENTLY, and conflating the two is
 * easy because both end up looking like "the flag is off":
 *
 *   - An unregistered name is a programming error. `featureFlagService.get()`
 *     throws, so a typo surfaces immediately instead of silently reading as
 *     off. Iframe tools are the one exception: the `getAppConfigFeatureFlags`
 *     CrossFrame listener catches that throw and returns
 *     `{ enabled: false, error }`, so one bad name cannot fail a whole batch.
 *
 *   - A registered flag with no usable value -- AppConfig outage, value not yet
 *     deployed, or a store-scoped read with no storeId -- resolves to the
 *     `defaultEnabled` below with `source: 'default'`. This is the fail-open
 *     path, so keep `defaultEnabled` OFF: a failure must render the
 *     pre-feature experience, never the gated one.
 */

import { FeatureFlagRegistry } from './featureFlags.types'

export const FEATURE_FLAG_REGISTRY: FeatureFlagRegistry = {
    profile_settings_v1: {
        description: 'Profile & notification settings page (bottom sheet)',
        defaultEnabled: false,
        isStoreScoped: false,
        metadata: { cti: 'JWO/Store Management/Platform' },
    },
    push_notifications_v1: {
        description: 'Push notification device registration and settings',
        defaultEnabled: false,
        isStoreScoped: false,
        metadata: { cti: 'JWO/Store Management/Platform' },
    },
    // Temporary production-like validation flag for per-store AppConfig evaluation.
    // Remove after the per-store rollout validation completes.
    test_per_store_featureflag: {
        description: 'Test flag to demonstrate per-store feature flag behavior',
        defaultEnabled: false,
        isStoreScoped: true,
        metadata: { cti: 'JWO/Store Management/Platform' },
    },
    merchant_creation_v2: {
        description: 'V2 merchant creation flow with guided bringup',
        defaultEnabled: false,
        isStoreScoped: true,
        metadata: { cti: 'JWO/Store Management/Platform' },
    },
    golden_gate_in_app_notification_page_v2: {
        description: 'Action Center backed by the Gateway ListNotifications proxy (replaces the Virgo-fed notification center)',
        defaultEnabled: false,
        isStoreScoped: false,
        metadata: { cti: 'JWO/Store Management/Platform' },
    },
    view_only_tools_v1: {
        description: 'Open viewOnlySupported tools (Planogram) read-only for stores the user has not scanned into',
        defaultEnabled: false,
        isStoreScoped: false,
        metadata: { cti: 'JWO/Store Management/Platform' },
    },
    notification_drawer_close_button_v1: {
        description: 'Show the X close button in the notification drawer (NotificationsSheet) header. P525189101',
        defaultEnabled: false,
        isStoreScoped: false,
        metadata: { cti: 'JWO/Store Management/Platform', sim: 'P525189101' },
    },
    // Gates the IA, not a renderer: the same navigation and layout change ships on
    // Cloudscape (desktop) and Meridian (handheld), so one flag covers both.
    user_management_ia_v1: {
        description: 'New information architecture for the User Management Team tab (Team members and Identity providers), read by JwoUserManagementTool over CrossFrame',
        defaultEnabled: false,
        isStoreScoped: false,
        metadata: { cti: 'JWO/Store Management/Platform' },
    },
    shv_mobile_v1: {
        description: 'Show Store Health View in SMP mobile',
        defaultEnabled: false,
        isStoreScoped: false,
        metadata: { cti: 'IHM/Services/JwoStoreHealthService' },
    },
} as const

/** All registered flag names (derived, not manually maintained) */
export const ALL_FLAG_NAMES = Object.keys(FEATURE_FLAG_REGISTRY) as ReadonlyArray<string>

/** Type-safe flag name union */
export type FeatureFlagName = keyof typeof FEATURE_FLAG_REGISTRY
